import * as React from "react";
import { Box, Button, MenuItem, Select, useTheme } from "@mui/material";
import { AddRounded } from "@mui/icons-material";
import { AdmLayout } from "../../../layouts/base/adm.layout";
import { SimpleSnackbar } from "../../../components/snackbar";
import { CouponModel } from "../../../services/coupon/coupon.services";
import { AdminFilterBar, AdminPageHeader, AppModal, countLabel, DataTable, EmptyState } from "../../../components/ui";
import { CouponForm } from "./form/coupon-form";
import { useCouponPage } from "./use-coupon-page";
import { buildCouponColumns } from "./coupon-columns";

export const CouponPage: React.FC = () => {
    const theme = useTheme();
    const {
        loading,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        coupons,
        modalOpen,
        search,
        setSearch,
        statusFilter,
        setStatusFilter,
        selectedCoupon,
        formRef,
        displayedCoupons,
        handlePageChange,
        handlePageSizeChange,
        handleSwitchCoupon,
        handleCouponSaved,
        handleOpenCreate,
        handleOpenEdit,
        handleCloseModal,
    } = useCouponPage();

    const columns = buildCouponColumns({ theme, onToggle: handleSwitchCoupon, onEdit: handleOpenEdit });

    return (
        <AdmLayout loading={loading} hasLogo={true}>
            <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

            <Box sx={{ width: "100%", p: { xs: "18px 16px 24px", md: 4 } }}>
                <AdminPageHeader
                    title="Cupons"
                    subtitle="Gestão de cupons"
                    action={
                        <Button
                            variant="contained"
                            startIcon={<AddRounded />}
                            onClick={handleOpenCreate}
                            sx={{ height: 44, borderRadius: "12px", fontWeight: 600 }}
                        >
                            Novo cupom
                        </Button>
                    }
                />

                <AdminFilterBar
                    search={search}
                    onSearch={(v) => setSearch(v)}
                    placeholder="Buscar por código..."
                    filters={
                        <Select
                            size="small"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as "all" | "valid" | "expired")}
                            sx={{ height: 40, minWidth: 160, borderRadius: "10px" }}
                        >
                            <MenuItem value="all">Todos</MenuItem>
                            <MenuItem value="valid">Válido</MenuItem>
                            <MenuItem value="expired">Expirado</MenuItem>
                        </Select>
                    }
                    count={countLabel(displayedCoupons.length, coupons.pagination.total)}
                />

                <DataTable<CouponModel>
                    columns={columns}
                    rows={displayedCoupons}
                    countLabel={countLabel(displayedCoupons.length, coupons.pagination.total)}
                    rowKey={(row) => String(row.id)}
                    loading={loading}
                    page={coupons.pagination.currentPage}
                    pageSize={coupons.pagination.perPage}
                    totalRows={coupons.pagination.total}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={[5, 10, 25]}
                    minWidth={800}
                    emptyState={
                        <EmptyState
                            title="Nenhum cupom encontrado"
                            description="Crie um novo cupom para começar."
                            actionLabel="Novo cupom"
                            onAction={handleOpenCreate}
                        />
                    }
                />
            </Box>

            <AppModal
                open={modalOpen}
                onClose={handleCloseModal}
                kicker={selectedCoupon ? "Editar" : "Cadastrar"}
                title={selectedCoupon ? "Editar cupom" : "Novo cupom"}
                maxWidth={680}
                footer={
                    <>
                        <Button variant="outlined" onClick={handleCloseModal} sx={{ height: 40, borderRadius: "10px" }}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={() => formRef.current?.submit()} sx={{ height: 40, borderRadius: "10px" }}>
                            {selectedCoupon ? "Salvar alterações" : "Cadastrar"}
                        </Button>
                    </>
                }
            >
                <CouponForm ref={formRef} onCouponSaved={handleCouponSaved} initialCoupon={selectedCoupon} />
            </AppModal>
        </AdmLayout>
    );
};
